import { ColumnsType } from 'antd/es/table';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Button, message, Col, List, Row, Table, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { ListItemClickable } from '../components/ListItemClickable';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import {
  getDuplicateDonors,
  ignoreDuplicate,
  merge,
  useMerge,
} from '../slices/donor';
import { getClassification } from '../slices/classification';

const useStyles = createUseStyles({
  container: {
    height: '100%',
    padding: '20px',
  },
  list: {
    backgroundColor: '#FFFFFF',
  },
  listContainer: {
    height: '100%',
    maxHeight: '100%',
    overflowY: 'auto',
  },
  tableContainer: {
    height: '100%',
    maxHeight: '100%',
    overflowY: 'auto',
    '& .ant-table': {
      fontSize: '0.875vw',
    },
  },
  columnHeader: {
    fontWeight: 'bold',
    padding: '16px',
  },
  columnFieldName: {
    fontWeight: 'bold',
  },
  column: {
    padding: '0 !important',
  },
  cellContainer: {
    padding: '16px',
  },
  refresh: {
    display: 'block',
    margin: 'auto',
    padding: '0.25vw 0.9375vw',
    height: '2vw',
    fontSize: '0.875vw',
    '& span:last-child': {
      width: '90%',
      textOverflow: 'ellipsis',
      // overflow: 'clip',
      whiteSpace: 'nowrap',
      margin: '0 !important',
    },
    '& span:first-child': {
      margin: 'auto',
      height: '2vw',
    },
  },
  button: {
    display: 'block',
    padding: '0.25vw 0.9375vw',
    height: '2vw',
    fontSize: '0.875vw',
    float: 'right',
  },
});

const fields = [
  'name',
  'description',
  'object',
  'headquartersAddress',
  'creationYear',
  'statuses',
  'homepageUrl',
  'linkedinUrl',
  'hostedBy',
  'contactEmail',
  'contactPhone',
  'averageDonation',
  'currentYearBudget',
  'usesCallsToTender',
];

const isEmptyValue = (value: any) =>
  ['', null, undefined].includes(value) ||
  (Array.isArray(value) && value.length === 0);

export const DonorsMergeDuplicates: React.VFC = () => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { t } = useTranslation();

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [selectedFields, setSelectedFields] = useState<
    Record<string, Record<string, boolean>> | undefined
  >(undefined);

  const { duplicateDonors, loading, statuses } = useAppSelector(
    ({ donor, classification }) => ({
      duplicateDonors: donor.duplicateDonors,
      loading: donor.loading,
      statuses: classification.classification?.statuses,
    }),
  );

  const selectedDonor =
    duplicateDonors && selectedIndex > -1
      ? duplicateDonors[selectedIndex]
      : null;

  useEffect(() => {
    if (!duplicateDonors) {
      dispatch(getDuplicateDonors());
    }
    dispatch(getClassification());
  }, []);

  useEffect(() => {
    if (selectedDonor && duplicateDonors) {
      const buildSelectedFields: any = duplicateDonors[selectedIndex].reduce(
        (accumulator, current) => ({
          ...accumulator,
          [current._id]: {},
        }),
        {},
      );
      const donorsSources = selectedDonor.map((donor) => donor.sources || []);

      fields.forEach((fieldName) => {
        let priorityOrder: string[] | undefined;

        if (fieldName === 'object') {
          priorityOrder = ['Data.gouv', 'Joafe', 'CFF'];
        } else if (fieldName === 'statuses') {
          priorityOrder = ['CFF', 'Fondations.org', 'Opale'];
        } else if (fieldName === 'hostedBy') {
          priorityOrder = ['Fondations.org', 'CFF'];
        }

        const selectedSourcePriority = priorityOrder?.find((source) =>
          donorsSources.some(
            (sources, i) =>
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              !isEmptyValue(selectedDonor[i][fieldName]) &&
              sources.includes(source),
          ),
        );

        selectedDonor.some((donor: any, index) => {
          if (!isEmptyValue(donor[fieldName])) {
            if (
              (selectedSourcePriority &&
                donorsSources[index].includes(selectedSourcePriority)) ||
              !selectedSourcePriority
            ) {
              buildSelectedFields[donor._id][fieldName] = true;
              return true;
            }
          }
        });
      });
      setSelectedFields(buildSelectedFields);
    }
  }, [selectedDonor]);

  const handleUserKeyPress = useCallback(
    (key: KeyboardEvent) => {
      if (key.code === 'Enter' && selectedDonor) {
        handleValidateMerge();
      }
    },
    [selectedFields, selectedDonor],
  );

  useEffect(() => {
    window.addEventListener('keypress', handleUserKeyPress);

    return () => {
      window.removeEventListener('keypress', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  const computeFieldValues = (value: any, record: any) => {
    switch (record.fieldName) {
      case 'statuses':
        return value
          .map((statusId: string) =>
            t(
              `statuses:${
                statuses?.find((status) => status._id === statusId)?.name
              }`,
            ),
          )
          .join(', ');
      case 'description':
      case 'object':
        if (!['', null, undefined].includes(value)) {
          return value?.substring(0, 200) + (value?.length > 200 ? '...' : '');
        }
        break;
    }
    return value;
  };

  const handleUseMerge = (selectedId: string) => {
    // Modal.confirm({
    //   content:
    //     'Voulez vous vraiment utiliser les données de ce mécène ? Tous les autres ne seront plus visible.',
    //   onOk: () => {
    if (duplicateDonors && selectedIndex > -1) {
      dispatch(
        useMerge({
          selectedId,
          index: selectedIndex,
        }),
      );
      setSelectedIndex(-1);
      setSelectedFields(undefined);
    }
    // },
    // });
  };

  const handleIgnoreDuplicate = (ignoreId: string) => {
    // Modal.confirm({
    //   content:
    //     'Voulez vous vraiment ignorer ce doublon ? Il ne sera plus proposé dans la fenêtre de fusion.',
    //   onOk: () => {
    if (duplicateDonors && selectedIndex > -1) {
      dispatch(
        ignoreDuplicate({
          ignoreId,
          index: selectedIndex,
        }),
      );
      setSelectedIndex(-1);
      setSelectedFields(undefined);
    }
    //   },
    // });
  };

  const handleValidateMerge = () => {
    // Modal.confirm({
    //   content:
    //     'Voulez vous vraiment valider le merge ? Tous les champs non sélectionnés seront supprimés.',
    //   onOk: () => {
    if (selectedDonor && selectedFields && duplicateDonors) {
      const payload: any = {
        mergeIds: selectedDonor.map((donor) => donor._id),
      };

      fields.forEach((fieldName) => {
        selectedDonor.some((donor, donorIndex) => {
          const donorId = donor._id;

          if (selectedFields[donorId][fieldName]) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            payload[fieldName] = selectedDonor[donorIndex][fieldName];
            return true;
          }
        });
        if (!payload[fieldName]) {
          payload[fieldName] = null;
        }
      });

      if (!payload['name']) {
        delete payload['name'];
      }
      if (!payload['statuses']) {
        payload['statuses'] = [];
      }

      payload['sources'] = [];
      selectedDonor.forEach((donor) => {
        (donor.sources || []).forEach((source) => {
          if (!payload['sources'].includes(source)) {
            payload['sources'].push(source);
          }
        });
      });
      dispatch(
        merge({
          index: selectedIndex,
          id: duplicateDonors[selectedIndex][0]._id,
          payload,
        }),
      ).then((actionResult) => {
        if (merge.fulfilled.match(actionResult)) {
          message.success(`Merge effectué avec succès`);
        } else {
          message.error(`Erreur du merge. Log dans la console`);
          console.log(actionResult.error);
        }
      });
    }
  };

  const columns = useMemo<ColumnsType<any>>(
    () => [
      {
        dataIndex: 'fieldName',
        className: classes.columnFieldName,
      },
      ...(selectedDonor || []).map((donor) => ({
        dataIndex: donor._id,
        title: donor.name,
        className: classes.column,
        // eslint-disable-next-line react/display-name
        render: ({ value, donorId }: any, record: any) => {
          const computedValue = computeFieldValues(value, record);

          if (record.key === 'actions') {
            return (
              <div
                style={{
                  margin: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Button
                  style={{ marginBottom: '16px' }}
                  className={classes.button}
                  onClick={() => handleUseMerge(value)}
                >
                  Utiliser ce mécène
                </Button>
                <Button
                  onClick={() => handleIgnoreDuplicate(value)}
                  className={classes.button}
                  type="dashed"
                >
                  Ce n&apos;est pas un doublon
                </Button>
              </div>
            );
          } else if (record.key === 'linkedinUrl') {
            const hasLinkedinLink = selectedDonor?.some(
              (donor) =>
                ((donor?.sources?.includes('Linkedin') &&
                  donor?.linkedinUrl?.length) ||
                  0) > 0,
            );
            const isLinkedinSource = selectedDonor
              ?.find((donor) => donor._id === donorId)
              ?.sources?.includes('Linkedin');

            if (hasLinkedinLink) {
              return (
                <Typography.Text
                  style={{
                    fontWeight: isLinkedinSource ? 'bold' : 'normal',
                    padding: '16px',
                  }}
                >
                  {computedValue}
                </Typography.Text>
              );
            }
          }

          if (record.fieldName === 'sources') {
            return (
              <Typography.Text style={{ padding: '16px' }}>
                {computedValue}
              </Typography.Text>
            );
          }

          return (
            !isEmptyValue(computedValue) && (
              <ListItemClickable
                className={classes.cellContainer}
                isSelected={
                  !!selectedFields &&
                  selectedFields[donorId] &&
                  selectedFields[donorId][record.fieldName]
                }
                selectedColor="#8FBC8F"
                onClick={() => {
                  if (selectedFields) {
                    const otherDonors =
                      selectedDonor
                        ?.filter((donor) => donor._id !== donorId)
                        .map((donor) => donor._id) || [];

                    setSelectedFields({
                      [donorId]: {
                        ...selectedFields[donorId],
                        [record.fieldName]:
                          !selectedFields[donorId][record.fieldName],
                      },
                      ...otherDonors.reduce(
                        (accumulator, current) => ({
                          ...accumulator,
                          [current]: {
                            ...selectedFields[current],
                            [record.fieldName]: false,
                          },
                        }),
                        {},
                      ),
                    });
                  }
                }}
              >
                {computedValue}
              </ListItemClickable>
            )
          );
        },
      })),
    ],
    [selectedDonor, selectedFields],
  );
  const dataSource = useMemo(
    () => [
      {
        key: 'actions',
        ...selectedDonor?.reduce(
          (accumulator, current) => ({
            ...accumulator,
            [current._id]: { value: current._id },
          }),
          [],
        ),
      },
      {
        key: 'sources',
        fieldName: 'sources',
        ...selectedDonor?.reduce(
          (accumulator, current) => ({
            ...accumulator,
            [current._id]: {
              value: current.sources?.join(', '),
              donorId: current._id,
            },
          }),
          {},
        ),
      },
      ...fields
        .filter((field) => {
          return selectedDonor?.some(
            (donor: any) => !isEmptyValue(donor[field]),
          );
        })
        .map((field) => ({
          key: field,
          fieldName: field,
          ...selectedDonor?.reduce(
            (accumulator, current: any) => ({
              ...accumulator,
              [current._id]: {
                donorId: current._id,
                value: !Array.isArray(current[field])
                  ? current[field]?.toString()
                  : current[field],
              },
            }),
            {},
          ),
        })),
    ],
    [selectedDonor],
  );

  return (
    <div className={classes.container}>
      <Row style={{ maxHeight: '100%', height: '100%' }}>
        <Col span={5} className={classes.listContainer}>
          <List
            loading={loading}
            className={classes.list}
            bordered
            dataSource={duplicateDonors || []}
            header={
              <>
                <Button
                  style={{ width: '100%' }}
                  className={classes.refresh}
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setSelectedIndex(-1);
                    setSelectedFields(undefined);
                    dispatch(getDuplicateDonors());
                  }}
                  disabled={loading}
                >
                  Rafraîchir
                </Button>
                {!loading && (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: '0.875vw' }}
                  >
                    {duplicateDonors?.length} doublon(s)
                  </Typography.Text>
                )}
              </>
            }
            itemLayout="vertical"
            renderItem={(item, i) => (
              <ListItemClickable
                isSelected={
                  !!selectedDonor && selectedDonor[0]._id === item[0]._id
                }
                onClick={() => {
                  if (i !== selectedIndex) {
                    setSelectedIndex(i);
                  }
                }}
              >
                <Typography.Paragraph
                  ellipsis={{ rows: 1 }}
                  style={{ fontSize: '0.875vw' }}
                >
                  {item[0].name}
                </Typography.Paragraph>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: '0.875vw' }}
                >
                  {item.length} duplicatas possible
                </Typography.Text>
              </ListItemClickable>
            )}
          />
        </Col>
        <Col className={classes.tableContainer} span={19}>
          {selectedDonor && selectedFields && (
            <>
              <Table
                columns={columns}
                bordered
                dataSource={dataSource}
                pagination={false}
              />
              <Button
                type="primary"
                style={{ marginTop: '16px' }}
                className={classes.button}
                onClick={handleValidateMerge}
              >
                Valider le merge
              </Button>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};
